/**
 * ContentListComponent
 *
 * Reusable list component for displaying content items.
 * Matches Flutter ContentListWidget behavior.
 *
 * Features:
 * - Renders list of ContentListItems (each owns its own per-media handlers)
 * - Supports custom layouts (grid/list)
 * - Reusable across Feed, Profile, Search, etc.
 *
 * Video Management:
 * - Pauses / disposes videos as items leave the rendered window
 * - Reads fullscreen state to suspend virtualization while fullscreen is active
 *
 * Note: This uses ContentListItem which combines VideoComponent with the full
 * content UI. For simple video-only lists (e.g. in chat) use VideoComponent
 * directly.
 */

import React, { useCallback, useRef, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { ContentListItem } from './ContentListItem';
import { VirtualList, type VirtualListHandle } from '@/presentation/components/common/virtualList/VirtualList';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { RootState } from '@/presentation/store';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { MediaUtils } from '@/domain/utils/mediaUtils';

export interface ContentListComponentProps {
  /** Screen key that will be used for video management and identification.
   * Allows multiple ContentListComponents to coexist without video ID conflicts.
   * So, videos in different lists can have the same media ID without interfering with each other.
   */
  screenKey: string;

  /** List of media entities to display */
  medias: UserMediaEntity[];

  /** Current user ID */
  currentUserId?: string;

  /** Layout mode */
  layout?: 'grid' | 'list';

  /** Number of columns for grid layout */
  columns?: number;

  /** Whether to autoplay videos after initialization */
  autoplay?: boolean;

  /** Whether to show custom fullscreen button (for testing) */
  showFullscreenButton?: boolean;

  /** Whether to mute videos */
  muted?: boolean;

  /** Whether to show more button in user info */
  showMore?: boolean;

  /** Whether the entire list is loading */
  isLoading?: boolean;

  /** Whether there are more items to load */
  hasMore?: boolean;

  /** Custom CSS class */
  className?: string;

  /** Enable virtual scrolling for better performance with large lists */
  enableVirtualization?: boolean;

  /** Whether the list is currently active/visible (pause updates when inactive) */
  isActive?: boolean;

  /** Initial render window size as multiplier of viewport (default: 2 = 2x viewport) */
  initialWindowSize?: number;

  /** Gap between items */
  gap?: number;

  /** Show a separator between list items. When true, gap is handled by the separator div. Default: true */
  showListSeparator?: boolean;

  /**
   * Initial index to scroll to on mount.
   *
   * **Performance Note**: When provided, media items from index 0 up to initialIndex + buffer
   * will be rendered off-screen during initial mount to measure their actual heights. This ensures
   * accurate scroll positioning for content with variable heights (portrait/landscape videos,
   * different description lengths). After measurement (~100-300ms), normal virtualization resumes.
   *
   * Only items BEFORE the target are measured - performance scales with initialIndex value.
   */
  initialIndex?: number;

  /**
   * Height (px) of the page's sticky top chrome (its `ExpauseAppBar`), forwarded
   * verbatim to `VirtualList.scrollOffsetTop`.
   *
   * A content page whose bar overlays this list must pass the bar's MEASURED
   * height (`useMeasuredHeight`) or an `initialIndex` / `scrollToIndex` landing
   * puts the target media's author row behind the bar. Leave unset on pages
   * with no such chrome. Default (in `VirtualList`): 0.
   */
  scrollOffsetTop?: number;

  /** Callback when visible items change (for virtual scrolling) */
  onVisibleItemsChange?: (
    visibleItems: UserMediaEntity[],
    visibleStartIndex: number,
    visibleEndIndex: number,
    renderedItems: UserMediaEntity[],
    renderedStartIndex: number,
    renderedEndIndex: number
  ) => void;

  /** Custom empty state component */
  emptyState?: React.ReactNode;

  /** Function to check if user is subscribed to creator */
  isSubscribed?: (userId: string) => boolean;

  /** Container element for sheet portals (optional, for page-scoped sheets) */
  sheetContainer?: HTMLElement | null;

  /** Called when the user scrolls near the end of the list (for infinite scroll). */
  onReachedEnd?: () => void;

  /** Distance in pixels before the actual end to trigger `onReachedEnd`. Default: 200 */
  reachedEndThreshold?: number;

  /**
   * Optional QA tag forwarded to the underlying list root (`data-qa-id`) so a
   * feature list can be named for the `qa-tester` agent. Individual rows are
   * tagged inside `ContentListItem` with their entity id.
   */
  qa?: QaAttrOptions;
}

export const ContentListComponent = forwardRef<VirtualListHandle, ContentListComponentProps>(({
  screenKey,
  medias,
  currentUserId,
  layout = 'list',
  columns = 1,
  autoplay = false,
  showFullscreenButton = false,
  muted = false,
  showMore = false,
  isLoading = false,
  hasMore = false,
  className = '',
  enableVirtualization = true,
  isActive = true,
  initialWindowSize = 2,
  gap = 36,
  showListSeparator = true,
  initialIndex,
  scrollOffsetTop,
  onVisibleItemsChange,
  emptyState,
  isSubscribed,
  sheetContainer,
  onReachedEnd,
  reachedEndThreshold,
  qa,
}, ref) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const videoManagement = useVideoManagement();

  // Read fullscreen state to suspend virtualization while fullscreen is open.
  const isFullscreenOpen = useSelector((state: RootState) => state.videoManagement.fullscreen.isFullscreen);

  // Track rendered items for virtualization
  const lastRenderedStartIndex = useRef<number | null>(null);
  const lastRenderedEndIndex = useRef<number | null>(null);

  const handleVisibleItemsChange = useCallback((
    visibleItems: UserMediaEntity[],
    visibleStartIndex: number,
    visibleEndIndex: number,
    renderedItems: UserMediaEntity[],
    renderedStartIndex: number,
    renderedEndIndex: number
  ) => {
    if (!isActive) {
      return;
    }

    // Pause non-visible rendered videos.
    // forced=true so that they are not resumed when they get visible again on
    // scroll, or when the page becomes active again (back-navigation).
    const nonVisibleRenderedItems = renderedItems.filter(
      item => !visibleItems.some(visibleItem => visibleItem.id === item.id)
    );
    nonVisibleRenderedItems.forEach(item => {
      const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, item.id);
      videoManagement.pauseVideo(videoKey, { forced: true });
    });

    // Dispose videos for items that left the rendered window since the last update.
    const unmountedItems: UserMediaEntity[] = [];
    if (lastRenderedStartIndex.current !== null && lastRenderedEndIndex.current !== null) {
      for (let i = lastRenderedStartIndex.current; i < lastRenderedEndIndex.current; i++) {
        if (i < renderedStartIndex || i > renderedEndIndex) {
          if (i < 0 || i >= medias.length) continue;
          const item = medias[i];
          if (item) unmountedItems.push(item);
        }
      }
    }
    unmountedItems.forEach(item => {
      const videoKey = MediaUtils.composeVideoPlayerKey(screenKey, item.id);
      videoManagement.disposeVideo(videoKey);
    });

    lastRenderedStartIndex.current = renderedStartIndex;
    lastRenderedEndIndex.current = renderedEndIndex;

    onVisibleItemsChange?.(
      visibleItems,
      visibleStartIndex,
      visibleEndIndex,
      renderedItems,
      renderedStartIndex,
      renderedEndIndex
    );
  }, [screenKey, isActive, medias.length, videoManagement, onVisibleItemsChange]);

  const renderContentItem = useCallback((media: UserMediaEntity) => {
    const isThisItemLastItem = medias[medias.length - 1].id === media.id;

    const content = (
      <ContentListItem
        media={media}
        screenKey={screenKey}
        currentUserId={currentUserId}
        subscribed={isSubscribed?.(media.firebaseId || '') || false}
        showMore={showMore}
        showFullscreenButton={showFullscreenButton}
        autoplay={autoplay}
        muted={muted}
        sheetContainer={sheetContainer ?? null}
      />
    );

    if (!showListSeparator || isThisItemLastItem) {
      return content;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {content}
        <div style={{ height: gap, display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1, height: responsive.spacing.xxxs, backgroundColor: theme.border, marginLeft: responsive.spacing['3xl'], marginRight: responsive.spacing['3xl'] }} />
        </div>
      </div>
    );
  }, [
    screenKey,
    currentUserId,
    isSubscribed,
    showMore,
    showFullscreenButton,
    autoplay,
    muted,
    sheetContainer,
    medias,
    showListSeparator,
    gap,
    responsive,
    theme,
  ]);

  // Empty state — placed after all hooks to avoid "rendered fewer hooks" React error
  if (medias.length === 0 && !isLoading && !hasMore) {
    return (
      <div
        {...(qa ? qaAttr(qa) : {})}
        className="flex items-center justify-center py-12"
        style={{ color: theme.text.secondary }}
      >
        {emptyState || (
          <div className="text-center">
            <p className="text-lg mb-2">{l10n.content.noVideosAvailable}</p>
          </div>
        )}
      </div>
    );
  }

  // Grid layout
  if (layout === 'grid') {
    return (
      <>
        {enableVirtualization ? (
          <VirtualList
            ref={ref}
            items={medias}
            renderItem={(media) => renderContentItem(media)}
            getItemKey={(media) => `${screenKey}-virtual-grid-item-${media.id}`}
            initialWindowSize={initialWindowSize}
            // pause virtualization if parent screen not active or if fullscreen opened
            isActive={isActive && !isFullscreenOpen}
            gap={gap}
            initialIndex={initialIndex}
            scrollOffsetTop={scrollOffsetTop}
            className={className}
            onVisibleItemsChange={handleVisibleItemsChange}
            onReachedEnd={onReachedEnd}
            reachedEndThreshold={reachedEndThreshold}
            qa={qa}
          />
        ) : (
          <div
            {...(qa ? qaAttr(qa) : {})}
            className={`grid gap-4 ${className}`}
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
            }}
          >
            {medias.map((media) => renderContentItem(media))}
          </div>
        )}
      </>
    );
  }

  // List layout (vertical stack)
  return (
    <>
      {enableVirtualization ? (
        <VirtualList
          ref={ref}
          items={medias}
          renderItem={(media) => renderContentItem(media)}
          getItemKey={(media) => `${screenKey}-virtual-list-item-${media.id}`}
          initialWindowSize={initialWindowSize}
          isActive={isActive}
          gap={showListSeparator ? 0 : gap}
          initialIndex={initialIndex}
          scrollOffsetTop={scrollOffsetTop}
          className={className}
          onVisibleItemsChange={handleVisibleItemsChange}
          onReachedEnd={onReachedEnd}
          reachedEndThreshold={reachedEndThreshold}
          qa={qa}
        />
      ) : (
        <div {...(qa ? qaAttr(qa) : {})} className={`flex flex-col space-y-8 ${className}`}>
          {medias.map((media) => renderContentItem(media))}
        </div>
      )}
    </>
  );
});
